import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useTrial } from '../contexts/TrialContext';
import { Eye, EyeOff } from 'lucide-react';
import { BANNED_MESSAGE, isBannedError } from '../lib/banned';

// iter63c — friendly mapping from backend error codes returned by the
// Google OAuth callback (e.g. ?error=google_token_failed). Helps the user
// (and us) see *which step* of the OAuth handshake broke instead of the
// vague "google_auth_error".
const GOOGLE_ERROR_MESSAGES = {
  google_consent_declined:    'You cancelled the Google sign-in. Please try again.',
  google_no_code:             "Google didn't return an authorization code. Please try again.",
  google_oauth_misconfigured: 'Google sign-in is not fully configured on the server. Please contact support.',
  google_token_http_failed:   'Could not reach Google to complete sign-in. Check your connection and try again.',
  google_token_failed:        'Google rejected the sign-in. The server keys may need a refresh — please contact support.',
  google_userinfo_http_failed:'Could not fetch your Google profile. Please try again.',
  google_no_email:            'Your Google account did not share an email address with us. Please use email sign-up instead.',
  google_local_user_failed:   'We could not create or sign you in. Please try again or contact support.',
  // Legacy / fallback
  google_auth_error:          'Google sign-in failed. Please try again or use email sign-in.',
  google_auth_failed:         'Google sign-in failed. Please try again or use email sign-in.',
  account_banned:             BANNED_MESSAGE,
};

export default function Login() {
  const { login } = useAuth();
  const { trialMode } = useTrial();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPw, setShowPw] = useState(false);
  const [error, setError] = useState('');
  const [errorCode, setErrorCode] = useState('');
  const [loading, setLoading] = useState(false);

  // Surface ?error=... from a failed Google OAuth callback, or ?banned=1 after a mid-session ban.
  useEffect(() => {
    const code = searchParams.get('error') || (searchParams.get('banned') ? 'account_banned' : '');
    if (code) {
      // Match exact known code, otherwise match by prefix (google_token_*).
      let msg = GOOGLE_ERROR_MESSAGES[code];
      if (!msg) {
        const prefixHit = Object.keys(GOOGLE_ERROR_MESSAGES).find(k => code.startsWith(k));
        msg = prefixHit ? GOOGLE_ERROR_MESSAGES[prefixHit] : 'Sign-in failed. Please try again.';
      }
      setError(msg);
      setErrorCode(code);
    }
  }, [searchParams]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const user = await login(email, password);
      // Return the user to where they were headed before being bounced to
      // login (e.g. /subscription?session_id=… after Stripe checkout).
      const next = searchParams.get('next');
      if (next && next.startsWith('/') && !next.startsWith('//')) {
        navigate(next);
      } else if (user.role === 'admin') {
        navigate('/admin');
      } else if (user.role === 'sales') {
        navigate('/admin/sales');
      } else {
        navigate(user.onboarding_completed === false ? '/onboarding' : '/dashboard');
      }
    } catch (err) {
      if (err.response?.status === 403 && err.response?.data?.detail === 'EMAIL_NOT_VERIFIED') {
        // Account exists but the signup email was never verified — send them
        // to the OTP screen (a fresh code is auto-sent there).
        navigate(`/verify-email?email=${encodeURIComponent(email)}`);
        return;
      }
      if (isBannedError(err)) { setError(BANNED_MESSAGE); setErrorCode('account_banned'); return; }
      setError(err.response?.data?.detail || 'Invalid credentials');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-neutral-50 flex flex-col">
      <div className="p-4">
        <Link to="/" className="flex items-center gap-2.5" data-testid="login-logo">
          <div className="w-9 h-9 rounded-xl flex items-center justify-center">
            <img src="/logo-navbar.png" alt="CELPIPMax" className="w-9 h-9" />
          </div>
          <span className="font-[Manrope] font-extrabold text-lg text-slate-900">CELPIPMax</span>
        </Link>
      </div>
      <div className="flex-1 flex items-center justify-center px-4 pb-16">
        <div className="w-full max-w-md">
          <div className="bg-white rounded-2xl shadow-sm border border-neutral-200 p-8">
            <h1 className="text-2xl font-bold text-neutral-900 mb-1" data-testid="login-title">Welcome back</h1>
            <p className="text-neutral-500 text-sm mb-6">Sign in to continue your CELPIP preparation</p>

            {error && (
              <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700" data-testid="login-error">
                <div>{error}</div>
                {errorCode && (
                  <div className="mt-1 text-[11px] text-red-500/80 font-mono" data-testid="login-error-code">
                    error code: <span data-testid="login-error-code-value">{errorCode}</span>
                  </div>
                )}
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-neutral-700 mb-1">Email</label>
                <input type="email" value={email} onChange={e => setEmail(e.target.value)} placeholder="you@example.com" required
                  className="w-full px-4 py-2.5 border border-neutral-300 rounded-lg text-sm focus:ring-2 focus:ring-primary/20 focus:border-primary outline-none transition" data-testid="login-email" />
              </div>
              <div>
                <label className="block text-sm font-medium text-neutral-700 mb-1">Password</label>
                <div className="relative">
                  <input type={showPw ? 'text' : 'password'} value={password} onChange={e => setPassword(e.target.value)} placeholder="Enter your password" required
                    className="w-full px-4 py-2.5 border border-neutral-300 rounded-lg text-sm focus:ring-2 focus:ring-primary/20 focus:border-primary outline-none transition pr-10" data-testid="login-password" />
                  <button type="button" onClick={() => setShowPw(!showPw)} className="absolute right-3 top-1/2 -translate-y-1/2 text-neutral-400">
                    {showPw ? <EyeOff size={16} /> : <Eye size={16} />}
                  </button>
                </div>
              </div>
              <div className="flex justify-end">
                <Link to="/forgot-password" className="text-xs text-primary hover:underline" data-testid="forgot-password-link">Forgot password?</Link>
              </div>
              <button type="submit" disabled={loading}
                className="w-full py-2.5 bg-primary text-white font-medium rounded-lg hover:bg-primary-700 transition disabled:opacity-60" data-testid="login-submit">
                {loading ? 'Signing in...' : 'Sign in'}
              </button>
            </form>

            <div className="relative my-6">
              <div className="absolute inset-0 flex items-center"><div className="w-full border-t border-neutral-200" /></div>
              <div className="relative flex justify-center text-xs"><span className="bg-white px-3 text-neutral-400">or continue with</span></div>
            </div>

            <a href={`${process.env.REACT_APP_BACKEND_URL}/api/auth/google`}
              className="w-full flex items-center justify-center gap-2.5 py-2.5 border border-neutral-300 rounded-lg hover:bg-neutral-50 transition text-sm font-medium text-neutral-700" data-testid="google-login-btn">
              <svg width="18" height="18" viewBox="0 0 48 48"><path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"/><path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"/><path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z"/><path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"/></svg>
              Sign in with Google
            </a>

            <p className="mt-6 text-center text-sm text-neutral-500">
              Don't have an account? <Link to={(() => { const n = searchParams.get('next'); return n && n.startsWith('/') && !n.startsWith('//') ? `/signup?next=${encodeURIComponent(n)}` : '/signup'; })()} className="text-primary font-medium hover:underline" data-testid="signup-link">{trialMode ? 'Start your trial' : 'Sign up free'}</Link>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
